import { ReactNode, useMemo } from 'react'
import { Box, Stack, ToggleButton, ToggleButtonGroup, Typography, useTheme } from '@mui/material'
import LocalFireDepartmentRoundedIcon from '@mui/icons-material/LocalFireDepartmentRounded'
import CloudRoundedIcon from '@mui/icons-material/CloudRounded'
import ParkRoundedIcon from '@mui/icons-material/ParkRounded'
import { EmissionTier, emissionTierOptions } from './index'
import WbSunnyRoundedIcon from '@mui/icons-material/WbSunnyRounded'
type EmissionTierSelectorProps = {
  value: EmissionTier
  onChange: (tier: EmissionTier) => void
  orientation?: 'horizontal' | 'vertical'
  size?: 'default' | 'compact'
  fullWidth?: boolean
}

type TierMeta = {
  icon: ReactNode
  tone: string
  accent: string
  shortLabel: string
  detail: string
}

const tierMeta: Record<EmissionTier, TierMeta> = {
  [EmissionTier.LOW]: {
    icon: <ParkRoundedIcon fontSize="small" />,
    tone: '#525e1bff',
    accent: '#a5d6a7',
    shortLabel: 'Low emissions',
    detail: '+1.5 - 2.0°C',
  },
  [EmissionTier.MODERATE]: {
    icon: <WbSunnyRoundedIcon fontSize="small" />,
    tone: '#a1900dff',
    accent: '#f4f990ff',
    shortLabel: 'Moderate emissions',
    detail: '+2 - 4°C',
  },
  [EmissionTier.HIGH]: {
    icon: <LocalFireDepartmentRoundedIcon fontSize="small" />,
    tone: '#b71c1c',
    accent: '#ef9a9a',
    shortLabel: 'High emissions',
    detail: '+4°C',
  },
}

const EmissionTierSelector = ({
  value,
  onChange,
  orientation = 'horizontal',
  size = 'default',
  fullWidth = false,
}: EmissionTierSelectorProps) => {
  const theme = useTheme()
  const compact = size === 'compact'
  const selectedEdgeBorder = orientation === 'vertical' ? 'borderTop' : 'borderLeft'
  const spacingStyles =
    orientation === 'vertical'
      ? {
          // '& .MuiToggleButtonGroup-grouped:not(:first-of-type)': { marginTop: 0, borderTop: '1px solid transparent' },
          '& .MuiToggleButtonGroup-grouped:not(:last-of-type)': { marginBottom: 0 },
          alignItems: 'stretch',
        }
      : {
          // '& .MuiToggleButtonGroup-grouped:not(:first-of-type)': { marginLeft: 0, borderLeft: '1px solid transparent' },
        }

  const widthStyles = useMemo(
    () =>
      fullWidth
        ? {
            width: '100%',
            '& .MuiToggleButton-root': {
              flex: 1,
              width: '100%',
            },
          }
        : {},
    [fullWidth],
  )

  return (
    <ToggleButtonGroup
      exclusive
      value={value}
      orientation={orientation}
      onChange={(_, next) => next && onChange(next)}
      aria-label="GHG emission scenario"
      sx={{
        gap: orientation === 'horizontal' ? 0 : 0,
        ...spacingStyles,
        ...widthStyles,
        '& .MuiToggleButton-root': {
          borderRadius: 2,
          borderColor: 'divider',
          textTransform: 'none',
          minWidth: compact ? 120 : 160,
          padding: compact ? '8px 10px' : '12px 14px',
          justifyContent: 'flex-start',
          transition: theme.transitions.create(['background-color', 'border-color', 'box-shadow'], {
            duration: theme.transitions.duration.shorter,
          }),
          '&:hover': {
            backgroundColor: theme.palette.action.hover,
          },
        },
        '& .Mui-selected': {
          borderColor: theme.palette.primary.main,
          backgroundColor: theme.palette.background.paper,
          boxShadow: theme.shadows[2],
        },
        '& .MuiToggleButtonGroup-grouped.Mui-selected:not(:first-of-type)': {
          [selectedEdgeBorder]: `1px solid ${theme.palette.primary.main}`,
        },
      }}>
      {emissionTierOptions.map(option => {
        const meta = tierMeta[option.name]
        return (
          <ToggleButton key={option.name} value={option.name} aria-label={option.label}>
            <Stack direction="row" spacing={compact ? 1 : 1.5} alignItems="center">
              <Box
                sx={{
                  width: compact ? 28 : 32,
                  height: compact ? 28 : 32,
                  borderRadius: '50%',
                  backgroundColor: meta.accent,
                  display: 'grid',
                  placeItems: 'center',
                  color: meta.tone,
                }}>
                {meta.icon}
              </Box>
              <Stack spacing={0.25} alignItems={orientation === 'vertical' ? 'flex-start' : 'center'}>
                <Typography variant={compact ? 'body2' : 'body1'} fontWeight={700}>
                  {meta.shortLabel}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {meta.detail}
                </Typography>
              </Stack>
            </Stack>
          </ToggleButton>
        )
      })}
    </ToggleButtonGroup>
  )
}

export default EmissionTierSelector
